/**
 * @copyright Copyright 2024-2024 Epic Systems Corporation
 * @file Section containing app resources
 * @module Epic.AppOrchard.Showroom.AppResourcesSection
 */

import { LinkIcon } from "@chakra-ui/icons";
import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Box,
	Flex,
	Heading,
	Image,
	Text,
} from "@chakra-ui/react";
import { showroomText } from "ao/chakraTheme";
import { AOLink } from "ao/components/Core";
import { IGalleryStageWithChildren, IShowroomAppDownloadModel } from "ao/types/showroom";
import { getBaseUrl } from "ao/utils/helpers";
import { FunctionComponent, memo, useMemo } from "react";

interface IProps {
	appDetails: IShowroomAppDownloadModel;
	stage: IGalleryStageWithChildren;
}

export const AppResourcesSection: FunctionComponent<IProps> = memo((props) => {
	const { appDetails, stage } = props;

	const baseUrl = useMemo(getBaseUrl, []);
	const color = stage.textColor ?? showroomText;

	return (
		<>
			{stage.resourceHeaderText && appDetails.resourceLinks.length > 0 && (
				<Accordion
					allowToggle
					border={`1px solid ${color}`}
					borderRadius="10px"
					marginTop="25px"
					defaultIndex={[0]}
				>
					<AccordionItem border="none">
						<Heading as="h4" fontFamily="Jost" color={color}>
							<AccordionButton>
								<Flex alignItems="center" flex="1">
									<Image
										src={`${baseUrl}Content/images/${stage.iconPath}`}
										fallback={<></>}
										maxH="32px"
										maxW="32px"
									/>
									<Text whiteSpace={"nowrap"} ml="10px" fontSize="1.3rem" fontWeight="500">
										{stage.groupName} {appDetails.showroomYears.join(",")} Resources
									</Text>
								</Flex>
								<AccordionIcon fontSize="18px" />
							</AccordionButton>
						</Heading>
						<AccordionPanel>
							<Box
								whiteSpace={"pre-line"}
								dangerouslySetInnerHTML={{
									__html: stage.resourceHeaderText,
								}}
								paddingBottom="5px"
							/>

							{appDetails.resourceLinks.map((resource) => (
								<AOLink url={resource.url} display="block" variant="dark" isExternal>
									<LinkIcon mr="5px" />
									{resource.displayName}
								</AOLink>
							))}
						</AccordionPanel>
					</AccordionItem>
				</Accordion>
			)}
		</>
	);
});
